import {
  useState, useRef, useEffect 
} from 'react';
import {
  Loader2,
  FileText,
  CheckCircle,
  Clock,
  ExternalLink,
  X,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useDiscrepancyMessages, useSendMessage, useProposeResolution 
} from '@/hooks/use-discrepancies';
import type {
  Discrepancy, Message, ResolutionType 
} from '@/types/orders';
import {
  DISCREPANCY_TYPE_LABELS, RESOLUTION_TYPE_LABELS 
} from '@/types/orders';
import { StatusBadge } from './StatusBadge';
import { MessageBubble } from './MessageBubble';
import { ActionPanel } from './ActionPanel';
import { ImagePreviewModal } from './ImagePreviewModal';
import { InputArea } from './InputArea';

interface ResolutionRoomProps {
  readonly discrepancy: Discrepancy;
  readonly onViewPO?: (poId: string) => void;
  readonly compact?: boolean;
  readonly userRole?: 'supplier' | 'receiver';
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function groupMessagesByDate(msgs: Message[]) {
  const groups: Record<string, Message[]> = {};
  for (const msg of msgs) {
    const date = formatDate(msg.createdAt);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
  }
  return groups;
}

export function ResolutionRoom({
  discrepancy,
  onViewPO,
  compact = false,
  userRole = 'supplier',
}: ResolutionRoomProps) {
  const [message, setMessage] = useState('');
  const [showActions, setShowActions] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [selectedAction, setSelectedAction] = useState<ResolutionType | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data, isLoading 
  } = useDiscrepancyMessages(discrepancy.discrepancyId);
  const sendMessage = useSendMessage();
  const proposeResolution = useProposeResolution();

  const messages = data?.data ?? [];
  const needsResponse = discrepancy.status === 'pending_supplier_response';
  const isResolved = discrepancy.status === 'resolved';
  const canTakeAction = userRole === 'supplier' && needsResponse;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!message.trim() && selectedImages.length === 0) return;
    const content =
      message.trim() || (selectedImages.length > 0 ? `Attached ${selectedImages.length} image(s)` : '');
    sendMessage.mutate({
      discrepancyId: discrepancy.discrepancyId,
      content,
    });
    setMessage('');
    setSelectedImages([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    setSelectedImages((prev) => [...prev, ...imageFiles].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeSelectedImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProposeResolution = () => {
    if (!selectedAction) return;
    const amount =
      selectedAction === 'credit_note' && creditAmount ? parseFloat(creditAmount) : undefined;

    proposeResolution.mutate(
      {
        discrepancyId: discrepancy.discrepancyId,
        payload: {
          resolution: selectedAction,
          creditNoteAmount: amount,
        },
      },
      {
        onSuccess: () => {
          setShowActions(false);
          setSelectedAction(null);
          setCreditAmount('');
        },
      }
    );
  };

  const groupedMessages = groupMessagesByDate(messages);
  const resolutionLabel = discrepancy.finalResolution ?? discrepancy.proposedResolution;

  return (
    <>
      <Card className={`flex flex-col ${compact ? 'h-[400px]' : 'h-[600px]'} border-slate-200`}>
        <ResolutionRoomHeader
          discrepancy={discrepancy}
          isResolved={isResolved}
          resolutionLabel={resolutionLabel}
          onViewPO={onViewPO}
        />

        <CardContent className="flex-1 overflow-y-auto p-4 bg-white">
          <MessagesContent
            isLoading={isLoading}
            messages={messages}
            groupedMessages={groupedMessages}
            onImageClick={setPreviewImage}
            messagesEndRef={messagesEndRef}
          />
        </CardContent>

        {showActions && canTakeAction && (
          <ActionPanel
            discrepancy={discrepancy}
            selectedAction={selectedAction}
            creditAmount={creditAmount}
            isPending={proposeResolution.isPending}
            onSelectAction={setSelectedAction}
            onCreditAmountChange={setCreditAmount}
            onCancel={() => {
              setShowActions(false);
              setSelectedAction(null);
            }}
            onSubmit={handleProposeResolution}
          />
        )}

        <SelectedImagesPreview images={selectedImages} onRemove={removeSelectedImage} />

        <InputArea
          message={message}
          inputRef={inputRef}
          fileInputRef={fileInputRef}
          isPending={sendMessage.isPending}
          canSend={message.trim().length > 0 || selectedImages.length > 0}
          canTakeAction={canTakeAction}
          showActions={showActions}
          onMessageChange={setMessage}
          onKeyDown={handleKeyDown}
          onSend={handleSend}
          onImageSelect={handleImageSelect}
          onShowActions={() => setShowActions(true)}
        />
      </Card>

      {previewImage && <ImagePreviewModal imageUrl={previewImage} onClose={() => setPreviewImage(null)} />}
    </>
  );
}

interface ResolutionRoomHeaderProps {
  readonly discrepancy: Discrepancy;
  readonly isResolved: boolean;
  readonly resolutionLabel: ResolutionType | undefined;
  readonly onViewPO?: (poId: string) => void;
}

function ResolutionRoomHeader({
  discrepancy,
  isResolved,
  resolutionLabel,
  onViewPO,
}: ResolutionRoomHeaderProps) {
  const itemCount = discrepancy.items.length;
  const itemLabel = itemCount === 1 ? 'item' : 'items';

  return (
    <CardHeader className="pb-3 border-b border-slate-100 flex-shrink-0 bg-slate-50/50">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <CardTitle className="text-base font-medium text-slate-900">
              {DISCREPANCY_TYPE_LABELS[discrepancy.type]}
            </CardTitle>
            <StatusBadge status={discrepancy.status} />
          </div>
          <p className="text-sm text-slate-500 truncate">
            {discrepancy.poNumber} • {itemCount} {itemLabel} affected
          </p>
        </div>
        {onViewPO && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewPO(discrepancy.poId)}
            className="flex-shrink-0 text-slate-600 border-slate-200 hover:bg-slate-50"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            View PO
          </Button>
        )}
      </div>

      <div className="mt-3 p-2 rounded-md bg-white border border-slate-100">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {discrepancy.items.map((item) => (
            <span key={item.lineId} className="text-slate-600">
              <span className="font-medium">{item.itemName}</span>
              <span className="text-slate-400 ml-1">
                (-{item.affectedQuantity} {item.unit})
              </span>
            </span>
          ))}
        </div>
      </div>

      {resolutionLabel && (
        <div
          className={`mt-3 p-3 rounded-md border ${
            isResolved ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {isResolved ? (
              <CheckCircle className="h-4 w-4 text-emerald-600" />
            ) : (
              <Clock className="h-4 w-4 text-slate-500" />
            )}
            <span className={`text-sm font-medium ${isResolved ? 'text-emerald-700' : 'text-slate-700'}`}>
              {isResolved ? 'Resolved' : 'Proposed'}: {RESOLUTION_TYPE_LABELS[resolutionLabel]}
            </span>
            {discrepancy.creditNoteAmount && (
              <span className={`text-sm ${isResolved ? 'text-emerald-600' : 'text-slate-500'}`}>
                • {discrepancy.currency} {discrepancy.creditNoteAmount.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      )}
    </CardHeader>
  );
}

interface MessagesContentProps {
  readonly isLoading: boolean;
  readonly messages: Message[];
  readonly groupedMessages: Record<string, Message[]>;
  readonly onImageClick: (url: string) => void;
  readonly messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

function MessagesContent({
  isLoading,
  messages,
  groupedMessages,
  onImageClick,
  messagesEndRef,
}: MessagesContentProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <FileText className="h-8 w-8 text-slate-300 mb-2" />
        <p className="text-slate-500 text-sm">No messages yet</p>
        <p className="text-slate-400 text-xs mt-1">Start the conversation</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {Object.entries(groupedMessages).map(([date, msgs]) => (
        <div key={date}>
          <div className="flex items-center gap-2 my-4">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs text-slate-400 font-medium">{date}</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>
          {msgs.map((msg) => (
            <MessageBubble key={msg.messageId} message={msg} onImageClick={onImageClick} />
          ))}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}

interface SelectedImagesPreviewProps {
  readonly images: File[];
  readonly onRemove: (index: number) => void;
}

function SelectedImagesPreview({
  images, onRemove 
}: SelectedImagesPreviewProps) {
  if (images.length === 0) return null;

  return (
    <div className="border-t border-slate-100 p-3 bg-slate-50">
      <div className="flex gap-2 flex-wrap">
        {images.map((file) => (
          <div key={`selected-image-${file.name}-${file.size}`} className="relative">
            <img
              src={URL.createObjectURL(file)}
              alt={file.name}
              className="h-16 w-16 object-cover rounded-md border border-slate-200"
            />
            <button
              type="button"
              onClick={() => onRemove(images.indexOf(file))}
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
